import type { GameId } from '@chessmate/protocol';
import { webOnlyRequested } from './parse.js';
import type { GameStage } from './types.js';

/**
 * Which games a build offers, in two independent layers on top of the edition's allow-list
 * (docs/proposals/mindgames-editions.md; the operator's design, 2026-10-16):
 *
 *  1. PRESENCE, a build-time environment variable (`S4K_GAMES`): is the game in this build at all? It can only
 *     narrow the edition's allow-list, never widen it: naming a game the edition may not have is a build error.
 *  2. READINESS, a feature flag per game (`disabled`, `beta` or `prod`): a default in the edition config, overridable
 *     with `VITE_GAME_STAGES`. Beta games are shown only to someone who opted in to beta.
 *
 * Everything here is pure; the runtime wiring (env, query string, local storage) lives in `index.ts`.
 */
export const GAME_STAGES: readonly GameStage[] = ['disabled', 'beta', 'prod'];
export const DEFAULT_STAGE: GameStage = 'prod';

export interface Parsed<T> {
  readonly value: T;
  /** Problems found; a build treats any as fatal, the running app ignores the bad token and carries on. */
  readonly errors: readonly string[];
}

const tokens = (raw: string | null | undefined): string[] =>
  (raw ?? '').split(',').map((t) => t.trim()).filter((t) => t !== '');

/**
 * `S4K_GAMES`: unset, blank or `*` means every game the edition allows; otherwise a comma list of game ids. Ids the
 * edition does not allow are errors (a flag can never add a game to an edition), and a repeat is ignored.
 */
export function parseIncluded(raw: string | null | undefined, allowList: readonly GameId[]): Parsed<readonly GameId[]> {
  const list = tokens(raw);
  if (list.length === 0 || (list.length === 1 && list[0] === '*')) return { value: [...allowList], errors: [] };
  const errors: string[] = [];
  const wanted = new Set<GameId>();
  for (const t of list) {
    if ((allowList as readonly string[]).includes(t)) wanted.add(t as GameId);
    else errors.push(`"${t}" is not a game this edition allows (${allowList.join(', ')})`);
  }
  return { value: allowList.filter((g) => wanted.has(g)), errors };
}

/** `VITE_GAME_STAGES`: `hearts=beta,whist=prod,bridge=disabled`. Unknown stages or games are errors. */
export function parseStages(raw: string | null | undefined, allowList: readonly GameId[]): Parsed<Partial<Record<GameId, GameStage>>> {
  const value: Partial<Record<GameId, GameStage>> = {};
  const errors: string[] = [];
  for (const t of tokens(raw)) {
    const [id, stage, ...rest] = t.split('=').map((x) => x.trim());
    if (!id || !stage || rest.length > 0) {
      errors.push(`"${t}" is not game=stage`);
      continue;
    }
    if (!(allowList as readonly string[]).includes(id)) errors.push(`"${id}" is not a game this edition allows`);
    else if (!(GAME_STAGES as readonly string[]).includes(stage)) errors.push(`"${stage}" is not a stage (${GAME_STAGES.join(', ')})`);
    else value[id as GameId] = stage as GameStage;
  }
  return { value, errors };
}

/**
 * The operator's REMOTE stages (`GET /api/game-stages`, docs/proposals/remote-game-stages.md), validated with the same parser as
 * `VITE_GAME_STAGES`. Two layers of tolerance, both deliberate:
 *  - a payload that is not `{ stages: { ... } }` is IGNORED WHOLE (returns null: nothing known, keep whatever was there);
 *  - inside a good payload, each entry is judged on its own: a bad stage, a non-string, or an id outside `allowList` is dropped and the
 *    good entries are kept.
 * `allowList` is the games already in THIS build, so a remote value can change a game's stage but can never add a game.
 */
export function parseRemoteStages(payload: unknown, allowList: readonly GameId[]): Partial<Record<GameId, GameStage>> | null {
  if (typeof payload !== 'object' || payload === null) return null;
  const stages = (payload as { stages?: unknown }).stages;
  if (typeof stages !== 'object' || stages === null || Array.isArray(stages)) return null;
  const pairs: string[] = [];
  for (const [id, stage] of Object.entries(stages)) {
    if (typeof stage !== 'string' || /[,=\s]/.test(id) || /[,=\s]/.test(stage)) continue;
    pairs.push(`${id}=${stage}`);
  }
  return parseStages(pairs.join(','), allowList).value;
}

/** Precedence of the stage layers above the edition default: the remote value beats `VITE_GAME_STAGES`, which beats the edition default. */
export function effectiveOverrides(
  build: Readonly<Partial<Record<GameId, GameStage>>>,
  remote: Readonly<Partial<Record<GameId, GameStage>>>,
): Partial<Record<GameId, GameStage>> {
  return { ...build, ...remote };
}

export interface RolloutInput {
  /** The edition's allow-list, in display order. */
  readonly allowList: readonly GameId[];
  /** Games in this build (`S4K_GAMES`); defaults to the whole allow-list. */
  readonly included?: readonly GameId[];
  /** The edition's default stages. */
  readonly stages?: Readonly<Partial<Record<GameId, GameStage>>>;
  /** Overrides on top of the defaults (`VITE_GAME_STAGES`). */
  readonly overrides?: Readonly<Partial<Record<GameId, GameStage>>>;
  /** Whether this person opted in to beta games. */
  readonly beta?: boolean;
}

/** A game's stage: an override, else the edition default, else `prod`. */
export function stageOf(id: GameId, input: Pick<RolloutInput, 'stages' | 'overrides'>): GameStage {
  return input.overrides?.[id] ?? input.stages?.[id] ?? DEFAULT_STAGE;
}

/**
 * The games to show: in the allow-list, in the build, and either `prod` or (`beta` and the person opted in). Order is the
 * allow-list's, whatever order anything else lists them in. A game outside the allow-list can never appear.
 */
export function visibleGames(input: RolloutInput): GameId[] {
  const included = new Set(input.included ?? input.allowList);
  return input.allowList.filter((id) => {
    if (!included.has(id)) return false;
    const stage = stageOf(id, input);
    return stage === 'prod' || (stage === 'beta' && input.beta === true);
  });
}

/**
 * `S4K_WEB_ONLY_GAMES`, the enable/disable switch for games that may exist only in the web / desktop build: off (unset, blank, `0`, `false`,
 * `off`) means none; `1`, `true`, `on`, `all` or `*` means every game the edition lists as web-only; otherwise a comma list of those ids.
 * An id the edition does not list as web-only is an error: a flag can never add a game to an edition.
 */
export function parseWebOnly(raw: string | null | undefined, webOnly: readonly GameId[]): Parsed<readonly GameId[]> {
  if (!webOnlyRequested(raw ?? undefined)) return { value: [], errors: [] };
  const list = tokens(raw);
  if (list.length === 1 && ['1', 'true', 'on', 'yes', 'all', '*'].includes(list[0]!.toLowerCase())) return { value: [...webOnly], errors: [] };
  const errors: string[] = [];
  const wanted = new Set<GameId>();
  for (const t of list) {
    if ((webOnly as readonly string[]).includes(t)) wanted.add(t as GameId);
    else errors.push(`"${t}" is not a web-only game this edition lists (${webOnly.length > 0 ? webOnly.join(', ') : 'none'})`);
  }
  return { value: webOnly.filter((g) => wanted.has(g)), errors };
}

export type BuildTarget = 'web' | 'native';

export interface BuildGames {
  /** Every game this build may show, in catalog order: the edition's games plus the enabled web-only ones, narrowed by `S4K_GAMES`. */
  readonly games: readonly GameId[];
  /** The web-only games that are in this build (always empty for a native build). */
  readonly webOnly: readonly GameId[];
  readonly errors: readonly string[];
}

/**
 * The single place the build decides which games exist in it, combining the edition allow-list, the web-only switch and `S4K_GAMES`.
 * A native target with the web-only switch on is an ERROR (the wrapper must never carry them), not a quiet omission, so a mistaken deploy
 * job fails loudly instead of shipping a bundle that differs from what was asked for.
 */
export function resolveBuildGames(input: {
  readonly games: readonly GameId[];
  readonly webOnly?: readonly GameId[];
  readonly target: BuildTarget;
  readonly gamesEnv?: string | null;
  readonly webOnlyEnv?: string | null;
}): BuildGames {
  const eligible = input.webOnly ?? [];
  const errors: string[] = [];
  let enabled: readonly GameId[] = [];
  if (webOnlyRequested(input.webOnlyEnv ?? undefined)) {
    if (input.target === 'native') errors.push('S4K_WEB_ONLY_GAMES is on, but this is a native build: web-only games never go into the Capacitor wrapper');
    else {
      const parsed = parseWebOnly(input.webOnlyEnv, eligible);
      errors.push(...parsed.errors.map((e) => `S4K_WEB_ONLY_GAMES: ${e}`));
      enabled = parsed.value;
    }
  }
  const allow = [...input.games, ...enabled];
  const included = parseIncluded(input.gamesEnv, allow);
  errors.push(...included.errors.map((e) => `S4K_GAMES: ${e}`));
  return { games: included.value, webOnly: enabled.filter((g) => included.value.includes(g)), errors };
}
